import type { StyleRule } from '@archipro-design/aria';
import { pxArrayToRem } from '@archipro-design/aria';
import { TextTypeEnum } from 'generated/graphql';
import { pxToRem } from '@archipro-design/aria';
import EditorConfig from './editor-config';

const NON_SCALING_CONTENT_FONTSIZE = '16px';

export const RichTextEditorNodeStyling: StyleRule<{
    textType: TextTypeEnum;
    isEmpty?: boolean;
    disabled?: boolean;
}> = ({ theme, textType, isEmpty, disabled }) => {
    const linkStyles = {
        color:
            theme.siteVariables.bodyColor ||
            theme.siteVariables.colors.charcoal['250'],
        textDecoration: 'underline',
    };
    return {
        color:
            theme.siteVariables.bodyColor ||
            theme.siteVariables.colors.charcoal['250'],
        fontSize: pxToRem(14),
        lineHeight: theme.siteVariables.lineHeightSmall,

        [theme.screen.laptop]: {
            fontSize: NON_SCALING_CONTENT_FONTSIZE,
            lineHeight: theme.siteVariables.lineHeightSmall,
        },

        pointerEvents: disabled ? 'none' : 'auto',

        ':active': {
            outline: 'none',
        },
        ':focus': {
            outline: 'none',
        },

        // Disable margin-bottom when textType is h3, it will only be one node element
        '& > *': {
            marginBottom: textType === TextTypeEnum.H3 ? 0 : undefined,
        },

        '& > *:last-child': {
            marginBottom: 0,
        },

        '& .editor-text-bold': {
            fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight,
        },
        '& .editor-text-italic': {
            fontStyle: 'italic',
        },
        '& .editor-text-underline': {
            textDecoration: 'underline',
        },
        [`& .${EditorConfig.theme.paragraph}`]: {
            marginTop: 0,
            fontSize: NON_SCALING_CONTENT_FONTSIZE,
        },
        '& .editor-link': {
            ...linkStyles,
            '&span': linkStyles,
            '&strong': linkStyles,
            '&i': linkStyles,
            '&em': linkStyles,
        },
        '& a.editor-link:link': {
            ...linkStyles,
        },
        '& .editor-heading-h2': {
            marginTop: 0,
            textAlign: 'center',
            color:
                theme.siteVariables.bodyColor ||
                theme.siteVariables.colors.charcoal['250'],
            fontSize: theme.siteVariables.fontSizes.subheading02,
            lineHeight: theme.siteVariables.lineHeightSmallest,
            letterSpacing: theme.siteVariables.letterSpacingMedium,
            ...theme.siteVariables.getTextStyle('Medium65', 'Article'),
            padding: pxArrayToRem([0, 16]),

            [theme.screen.laptop]: {
                fontSize: pxToRem(30),
                lineHeight: theme.siteVariables.lineHeightSmaller,
                letterSpacing: theme.siteVariables.letterSpacingSmall,
            },
        },
        '& .editor-heading-h3, & .editor-heading-h4': {
            marginTop: 0,
            marginBottom: theme.siteVariables.spaces['space-28'],
            color:
                theme.siteVariables.bodyColor ||
                theme.siteVariables.colors.charcoal['250'],
            fontSize: theme.siteVariables.fontSizes.caption01,
            lineHeight: 1.6,
            ...theme.siteVariables.textStyles.Text.Medium65,
        },
        [`& .${EditorConfig.theme.quote}`]: {
            margin: pxToRem(30) + ' auto',
            paddingTop: pxToRem(40),
            paddingBottom: pxToRem(40),
            fontWeight: 'normal',
            fontSize: theme.siteVariables.fontSizes.label01,
            lineHeight: theme.siteVariables.lineHeightSmallest,
            color:
                theme.siteVariables.bodyColor ||
                theme.siteVariables.colors.charcoal['250'],
            letterSpacing: theme.siteVariables.letterSpacingMedium,
            borderTop: `1px solid ${theme.siteVariables.bodyColor}`,
            borderBottom: `1px solid ${theme.siteVariables.bodyColor}`,
            width: '100%',
            // Other containers have 16rem horizontal padding
            maxWidth: 'calc(100% - 36rem)',

            [theme.screen.laptop]: {
                padding: pxArrayToRem([40, 0]),
                margin: pxToRem(40) + ' auto',
                fontSize: theme.siteVariables.fontSizes['subheading03-alt'],
                letterSpacing: theme.siteVariables.letterSpacingSmall,
                lineHeight: theme.siteVariables.lineHeightSmaller,
                maxWidth: pxToRem(850),
            },

            '&::before': {
                content: isEmpty ? '' : 'open-quote',
            },
            '&::after': {
                content: isEmpty ? '' : 'close-quote',
            },
        },
    };
};
